import { useMemo, useState } from 'react';
import composeTheme from '@theme/theme';
import { Box, ThemeProvider } from '@mui/material';
import { QuoteSearchRequestQuery, QuoteSearchResponse } from '@lio/api-types';
import * as styles from '@components/dataGrid/FullPageDataGrid.styles';
import {
  useCreateWatchedQuote,
  useDeletePinnedQuote,
  useDeleteWatchedQuote,
  useInsertPinnedQuote,
  useQuoteSearch,
  useUpdateQuote,
} from '@services/quotes';
import { CancelationModal, DeclinationModal } from '@components/modals';
import { FlagForReferralDialog } from '@components/dialog';
import { CopyQuoteDialog } from '@pages/QuoteDetails/dialogs/CopyQuoteDialog';
import { RoleManagementDialog, DuplicateQuoteDialog } from '@pages/QuoteDetails/dialogs';
import { ToastType } from '@components/toast';
import { useSnackbar } from 'notistack';
import { TableWithStore } from '@components/table/StoreTable/TableWithStore';

import { useStore } from '@store/GlobalStore';
import { shallow } from 'zustand/shallow';
import { SortQuoteQueryParam } from '@pages/Quotes/QuotesTable/SortQuoteQueryParam';

import { QuoteColumnField } from '@pages/Quotes/QuotesTable/QuoteColumnField.enum';
import { quoteTableFilterMetadataBuilder } from '@pages/Quotes/QuotesTable/quoteTableFilterMetadataBuilder';
import { ClockFormat } from '@lio/rule-engine';

import {
  getDefaultFilters,
  getColumns,
  getDefaultHiddenColumns,
  getQuickFilterGroups,
  getFilterQuoteQueryParam,
  getQuoteFilters,
} from './utils';
import { QuoteDataGridType, UnderwriterHomePageTabs } from '@pages/Quotes/QuotesTable/DataGrid.constants';
import { TActionItemCountPerType } from '@pages/Quotes/QuotesTable/utils/getQuickFilterGroups';

interface IQuoteSearchDataGrid {
  viewType: QuoteDataGridType;
  config?: {
    /** Used to bump the table id (ie change cache to refresh original state) */
    version?: string;
  };
  other?: {
    actionItemCountPerViewType?: TActionItemCountPerType;
  };
}

export const QuoteSearchDataGrid = ({ viewType, config, other }: IQuoteSearchDataGrid) => {
  const { userAuth, currentUser, layoutPreferences } = useStore((state) => state, shallow);
  const isInternalUser = userAuth.isInternalUser;
  const isUnderwriter = userAuth.isUnderwriter;
  const clockFormat = layoutPreferences.clockFormat as unknown as ClockFormat;

  const { enqueueSnackbar } = useSnackbar();

  const [cancelQuoteDetails, setCancelQuoteDetails] = useState({
    cancelQuoteId: '',
    cancelQuotePolicyNumber: '',
  });
  const [declineQuoteDetails, setDeclineQuoteDetails] = useState({
    declineQuoteId: '',
    declineQuotePolicyNumber: '',
    declineQuoteStatus: '',
    declineQuoteCustomerId: '',
    declineQuoteBrokerageId: '',
    hasInternalOnlyAccessRestriction: false,
  });
  const [flagForReferralDetails, setFlagForReferralDetails] = useState({
    quoteId: '',
  });
  const [copyQuoteDetails, setCopyQuoteDetails] = useState<{ quoteId?: string }>({
    quoteId: undefined,
  });
  const [roleManagementDetails, setRoleManagementDetails] = useState<{ quoteId?: string }>({
    quoteId: undefined,
  });
  const [duplicateQuoteDetails, setDuplicateQuoteDetails] = useState<{ quoteId?: string }>({
    quoteId: undefined,
  });

  const { updateQuote, isPending: isUpdateQuoteLoading } = useUpdateQuote();

  const handleOpenCancelQuoteDialog = (quoteId: string, policyNumber: string) => {
    setCancelQuoteDetails({
      cancelQuoteId: quoteId,
      cancelQuotePolicyNumber: policyNumber,
    });
  };

  const handleOpenDeclineQuoteDialog = (
    quoteId: string,
    policyNumber: string,
    quoteStatus: string,
    quoteCustomerId: string,
    quoteBrokerageId: string,
    hasInternalOnlyAccessRestriction: boolean,
  ) => {
    setDeclineQuoteDetails({
      declineQuoteId: quoteId,
      declineQuotePolicyNumber: policyNumber,
      declineQuoteStatus: quoteStatus,
      declineQuoteCustomerId: quoteCustomerId,
      declineQuoteBrokerageId: quoteBrokerageId,
      hasInternalOnlyAccessRestriction,
    });
  };

  const handleOpenCopyQuoteDialog = (quoteId: string) => {
    setCopyQuoteDetails({ quoteId });
  };
  const handleCloseCancelQuoteDialog = () => {
    setCancelQuoteDetails({
      cancelQuoteId: '',
      cancelQuotePolicyNumber: '',
    });
  };
  const handleCloseFlagForReferralDialog = () => {
    setFlagForReferralDetails({
      quoteId: '',
    });
  };
  const handleCloseDeclineQuoteDialog = () => {
    setDeclineQuoteDetails({
      declineQuotePolicyNumber: '',
      declineQuoteId: '',
      declineQuoteStatus: '',
      declineQuoteCustomerId: '',
      declineQuoteBrokerageId: '',
      hasInternalOnlyAccessRestriction: false,
    });
  };
  const handleOpenFlagForReferralDialog = (quoteId: string) => {
    setFlagForReferralDetails({
      quoteId,
    });
  };
  const handleOpenDuplicateQuoteDialog = (quoteId: string) => {
    setDuplicateQuoteDetails({ quoteId });
  };
  const handleCloseDuplicateQuoteDialog = () => {
    setDuplicateQuoteDetails({ quoteId: undefined });
  };

  const handleOpenRoleManagementDialog = (quoteId: string) => {
    setRoleManagementDetails({ quoteId });
  };
  const handleCloseRoleManagementDialog = () => {
    setRoleManagementDetails({ quoteId: undefined });
  };

  const { insertPinnedQuote } = useInsertPinnedQuote();
  const { deletePinnedQuote } = useDeletePinnedQuote();

  const { createWatchedQuote } = useCreateWatchedQuote();
  const { deleteWatchedQuote } = useDeleteWatchedQuote();

  const handleShowUpdateQuoteSnackbar = ({ isUpdateQuoteError }: { isUpdateQuoteError: boolean }) => {
    const snackbarMessage = `Updating quote roles has ${isUpdateQuoteError ? 'failed' : 'succeeded'}`;
    const updateQuoteResult = isUpdateQuoteError ? 'Error' : 'Success';
    const snackbarVariant = ToastType[updateQuoteResult];

    enqueueSnackbar(snackbarMessage, {
      preventDuplicate: true,
      variant: snackbarVariant,
    });
  };

  type TQuoteTable = {
    items: QuoteSearchResponse['items'];
    params: QuoteSearchRequestQuery;
  };

  const useQuoteSearchWrapper = (params: QuoteSearchRequestQuery) => {
    const searchParams = {
      ...defaultFilters.defaultFilters,
      ...params,
      ...defaultFilters.forcedFilters,
    };

    let result = useQuoteSearch(searchParams, true);
    return result;
  };

  const columns = useMemo(() => {
    return getColumns({
      viewType,
      userConfig: {
        currentUserId: currentUser?.id ?? '',
        userAuth,
        layoutPreferences: {
          clockFormat,
        },
      },
      modalConfig: {
        showCopyQuoteModal: handleOpenCopyQuoteDialog,
        showCancelQuoteModal: handleOpenCancelQuoteDialog,
        showDeclineQuoteModal: handleOpenDeclineQuoteDialog,
        showFlagForReferralModal: handleOpenFlagForReferralDialog,
        showRoleManagementModal: handleOpenRoleManagementDialog,
        showDuplicateQuoteModal: handleOpenDuplicateQuoteDialog,
        deletePinnedQuote,
        insertPinnedQuote,
        createWatchedQuote,
        deleteWatchedQuote,
        updateQuote,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewType, isUnderwriter, isInternalUser, currentUser?.id]);

  const columnSelector = columns.reduce((acc: any, col) => {
    acc[col.field] = col.headerName;
    return acc;
  }, {});

  const filterQueryParam = useMemo(() => {
    return getFilterQuoteQueryParam({
      currentUserId: currentUser?.id ?? '',
      underwriterId: currentUser?.underwriter?.id ?? '',
    });
  }, [currentUser?.id, currentUser?.underwriter?.id]);

  const { defaultHiddenColumns, quickFilterGroup, defaultFilters, filters } = useMemo(() => {
    return {
      defaultHiddenColumns: getDefaultHiddenColumns(viewType),
      quickFilterGroup: getQuickFilterGroups(viewType, {
        defaultFilter: isUnderwriter ? 'My Action Items' : 'All',
        actionItemCountPerViewType: other?.actionItemCountPerViewType,
      }),
      defaultFilters: getDefaultFilters(viewType),
      filters: getQuoteFilters({ viewType, isInternalUser }),
    };
  }, [viewType, other?.actionItemCountPerViewType, isInternalUser, isUnderwriter]);

  const isOnUWHomePage = UnderwriterHomePageTabs.includes(viewType);

  return (
    <ThemeProvider theme={composeTheme('light')}>
      <Box sx={{ ...styles.dataGridContainer }}>
        <TableWithStore<TQuoteTable>
          tableId={
            config?.version ? `quotes-table-store-${viewType}-v${config?.version}` : `quotes-table-store-${viewType}`
          }
          storeProps={{
            quickFilterGroup,
            tableFilterTypeMetaDataBuilder: quoteTableFilterMetadataBuilder,
            columnSelectorConfig: {
              columnSelector: {
                [viewType]: columnSelector,
              },
              selectedTab: viewType,
              defaultDisabledKey: QuoteColumnField.QuoteNumber,
              hiddenFromSelectionKeys: [QuoteColumnField.Menu],
              defaultHiddenColumns,
            },
          }}
          tableProps={{
            ariaLabel: `quotes-table-${viewType}`,
            onLoadData: useQuoteSearchWrapper,
            columns,
            isInitiating: false,
            filterConfig:
              viewType === QuoteDataGridType.UWHomePageRecentlyViewed
                ? {
                    filterQueryParam: {
                      searchText: 'searchText',
                    },
                    filters: [],
                  }
                : {
                    filterQueryParam,
                    filters,
                    displayMode: 'Modal',
                  },
            sortConfig: {
              sortConfigPrefix: {
                filterKey: 'toggle',
                sortKey: 'uwFlaggedAsImportant',
                sortDirection: '-',
              },
              sortQueryParam: SortQuoteQueryParam,
              defaultSort: { field: QuoteColumnField.QuoteNumber, sort: 'desc' },
            },
            rowConfig: {
              getRowId: ({ row }) => {
                return row?.id ?? '';
              },
              getRowClassName: ({ row, nextRow, isLastVisible }) => {
                let className = '';

                if (row.hasDuplicateQuotes) {
                  className = 'yellow-highlight-row';
                }
                if (row.uwFlaggedAsImportant) {
                  className += ' blue-highlight-row';

                  // Only add last-highlight-row if toggle=showImportantFirst is in the URL
                  const urlParams = new URLSearchParams(window.location.search);
                  const showImportantFirst = urlParams.get('toggle') === 'showImportantFirst';
                  if (showImportantFirst && !nextRow?.uwFlaggedAsImportant && !isLastVisible) {
                    className += ' last-highlight-row';
                  }
                }
                return className;
              },
            },
            sxProps: {
              dataGrid: {
                ...styles.dataGrid,
              },
            },
            paginationConfig: { currentPage: 0, rowsPerPage: 25, pageOffset: 0 },
            noResultsConfig: {
              ignoreFilters: ['uwTab'],
              noMatchButtonText: isOnUWHomePage ? 'View all Action Items?' : undefined,
              noResultsMessage: 'There are currently no items to display',
            },
          }}
        />

        <CancelationModal
          isOpen={!!cancelQuoteDetails.cancelQuoteId}
          quoteId={cancelQuoteDetails.cancelQuoteId}
          policyNumber={cancelQuoteDetails.cancelQuotePolicyNumber}
          onCloseModal={handleCloseCancelQuoteDialog}
        />
        <DeclinationModal
          isOpen={!!declineQuoteDetails.declineQuoteId}
          quoteId={declineQuoteDetails.declineQuoteId}
          policyNumber={declineQuoteDetails.declineQuotePolicyNumber}
          currentStatusId={declineQuoteDetails.declineQuoteStatus}
          customerId={declineQuoteDetails.declineQuoteCustomerId}
          brokerageId={declineQuoteDetails.declineQuoteBrokerageId}
          onCloseModal={handleCloseDeclineQuoteDialog}
          hasInternalOnlyAccessRestriction={declineQuoteDetails.hasInternalOnlyAccessRestriction ?? false}
        />
        {!!copyQuoteDetails.quoteId && (
          <CopyQuoteDialog
            quoteId={copyQuoteDetails.quoteId}
            handleClose={() => {
              setCopyQuoteDetails({ quoteId: undefined });
            }}
          />
        )}
        {!!flagForReferralDetails.quoteId && (
          <FlagForReferralDialog
            open={!!flagForReferralDetails.quoteId}
            handleClose={handleCloseFlagForReferralDialog}
            quoteId={flagForReferralDetails.quoteId}
          />
        )}
        {roleManagementDetails.quoteId ? (
          <RoleManagementDialog
            showDialog={Boolean(roleManagementDetails.quoteId)}
            quoteId={roleManagementDetails.quoteId}
            closeLinkDialog={handleCloseRoleManagementDialog}
            onUpdateQuote={updateQuote}
            isUpdateQuoteLoading={isUpdateQuoteLoading}
            onShowUpdateQuoteSnackbar={handleShowUpdateQuoteSnackbar}
          />
        ) : null}
        {duplicateQuoteDetails.quoteId ? (
          <DuplicateQuoteDialog
            showDialog={Boolean(duplicateQuoteDetails.quoteId)}
            quoteId={duplicateQuoteDetails.quoteId}
            closeLinkDialog={handleCloseDuplicateQuoteDialog}
            isUpdateQuoteLoading={isUpdateQuoteLoading}
          />
        ) : null}
      </Box>
    </ThemeProvider>
  );
};
